/**
 * The builder's left rail: the two areas (shared email layouts, message
 * templates), then the list for the chosen area. Every row comes from the API.
 * On a tablet or phone the whole rail sits in a left Drawer (MessageBuilder).
 */
import { Btn, Dropdown, Icon, MediaRow, SectionLabel, SegmentedControl } from "@v0y4ge/jrni-ui";
import type { MessageLayoutSummary } from "@v0y4ge/api-contracts";
import type { Failure } from "../api";
import { layoutChips, layoutIsProvided, railChips, templateChips, type CatalogueGroup, type CatalogueMessage, type Tr } from "../model";
import { Chips, FailureBanner, RailSkeleton } from "./common";

export type Area = "layouts" | "messages";

export interface RailProps {
  t: Tr;
  area: Area;
  onArea: (a: Area) => void;
  canWrite: boolean;
  /* messages */
  catalogue: { phase: "loading" | "error" | "ready"; groups: CatalogueGroup[]; failure: Failure | null; refreshing: boolean; reload: () => void };
  /** One entry per Language choice (`catalogueLanguageViews`); the Language list shows itself only with two or more. */
  locales: { value: string; label: string }[];
  locale: string;
  onLocale: (v: string) => void;
  /** The selected message's rail identity (`CatalogueMessage.key`). */
  selectedMessageKey: string | null;
  onMessage: (m: CatalogueMessage) => void;
  messageDirty: (m: CatalogueMessage) => boolean;
  /* layouts */
  layouts: { phase: "loading" | "error" | "ready"; rows: MessageLayoutSummary[]; failure: Failure | null; refreshing: boolean; reload: () => void };
  selectedLayoutKey: string | null;
  onLayout: (key: string) => void;
  layoutDirty: (key: string) => boolean;
  onNewLayout: (from: "default" | "blank") => void;
  newLayoutBusy: boolean;
  defaultPublished: boolean;
  /** The global default layout's name, for "Duplicate <name>". */
  defaultName: string | null;
}

export function MessageRail(p: RailProps) {
  const { t } = p;
  const nav = (
    <SegmentedControl<Area> full size="sm" ariaLabel={t("messaging.builder.rail.areas", "Template areas")} value={p.area} onChange={p.onArea} testid="msgb-areas"
      options={[
        { value: "layouts", label: t("messaging.builder.area.layoutsShort", "Layouts"), icon: <Icon.layers size={14} /> },
        { value: "messages", label: t("messaging.builder.area.messagesShort", "Messages"), icon: <Icon.mail size={14} /> },
      ]} />
  );
  const listLabel = p.area === "layouts" ? t("messaging.builder.rail.layoutsList", "Shared email layouts list") : t("messaging.builder.rail.messagesList", "Message templates list");
  return (
    <aside className="msgb-rail" aria-label={t("messaging.builder.rail.areas", "Template areas")}>
      <div className="msgb-railhead">{nav}</div>
      <div id="msgb-railbody" className="msgb-railbody" role="region" aria-label={listLabel} aria-busy={p.area === "layouts" ? p.layouts.refreshing : p.catalogue.refreshing}>
        {p.area === "layouts" ? <LayoutList {...p} /> : <MessageList {...p} />}
      </div>
    </aside>
  );
}

function MessageList(p: RailProps) {
  const { t, catalogue } = p;
  if (catalogue.phase === "loading") return <RailSkeleton rows={8} label={t("messaging.builder.loading.messages", "Loading message templates")} />;
  if (catalogue.phase === "error" && catalogue.failure && !catalogue.groups.length) {
    return <FailureBanner t={t} failure={catalogue.failure} onRetry={catalogue.reload} />;
  }
  return (
    <>
      {catalogue.phase === "error" && catalogue.failure ? <FailureBanner t={t} failure={catalogue.failure} onRetry={catalogue.reload} /> : null}
      {p.locales.length > 1 ? (
        <Dropdown label={t("messaging.builder.language", "Language")} options={p.locales} value={p.locale} onChange={(v) => p.onLocale(String(v))} />
      ) : null}
      {/* An empty catalogue is said once, by the editor column (MessageBuilder). */}
      {catalogue.groups.map((g) => (
        <div className="msgb-railblock" key={g.id}>
          <SectionLabel>{g.label}</SectionLabel>
          <ul className="msgb-list">
            {g.messages.map((m) => {
              const row = m.rows.email || m.rows.sms;
              const chips = row ? templateChips(row, t) : [];
              const channels = [m.rows.email ? t("messaging.builder.channel.email", "Email") : null, m.rows.sms ? t("messaging.builder.channel.sms", "SMS") : null].filter(Boolean).join(" + ");
              return (
                <MediaRow key={m.key} as="li" density="compact" title={m.name}
                  subtitle={<span className="msgb-railchips"><span>{channels}</span><Chips chips={railChips(chips, p.messageDirty(m), t)} /></span>}
                  selected={p.selectedMessageKey === m.key} aria-selected={p.selectedMessageKey === m.key}
                  onClick={() => p.onMessage(m)} testid={`msgb-row-${m.key}`} />
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );
}

function LayoutList(p: RailProps) {
  const { t, layouts } = p;
  const live = layouts.rows.filter((l) => !l.archived);
  const provided = live.filter(layoutIsProvided);
  const own = live.filter((l) => !layoutIsProvided(l));
  const archived = layouts.rows.filter((l) => l.archived);
  const row = (l: MessageLayoutSummary) => (
    <MediaRow key={l.layoutKey} as="li" density="compact" title={l.name}
      subtitle={<span className="msgb-railchips"><Chips chips={railChips(layoutChips(l, t, layoutIsProvided(l)), p.layoutDirty(l.layoutKey), t)} /></span>}
      selected={p.selectedLayoutKey === l.layoutKey} aria-selected={p.selectedLayoutKey === l.layoutKey}
      onClick={() => p.onLayout(l.layoutKey)} testid={`msgb-layout-${l.layoutKey}`} />
  );
  return (
    <>
      {p.canWrite ? (
        <div className="msgb-railblock">
          <SectionLabel>{t("messaging.builder.newLayout", "New layout")}</SectionLabel>
          <div className="msgb-actions msgb-actions--stack">
            <Btn variant="secondary" size="sm" full disabled={p.newLayoutBusy || !p.defaultPublished} onClick={() => p.onNewLayout("default")}
              title={p.defaultPublished ? undefined : t("messaging.builder.newLayout.noDefault", "There is no published global default to copy.")}>
              {p.defaultName ? `${t("messaging.builder.newLayout.duplicate", "Duplicate")} ${p.defaultName}` : t("messaging.builder.newLayout.duplicateDefault", "Duplicate the global default")}
            </Btn>
            <Btn variant="secondary" size="sm" full disabled={p.newLayoutBusy} onClick={() => p.onNewLayout("blank")}>{t("messaging.builder.newLayout.blank", "Blank")}</Btn>
          </div>
        </div>
      ) : null}
      {layouts.phase === "loading" ? <RailSkeleton rows={4} label={t("messaging.builder.loading.layouts", "Loading shared email layouts")} /> : (
        <>
          {layouts.phase === "error" && layouts.failure ? <FailureBanner t={t} failure={layouts.failure} onRetry={layouts.reload} /> : null}
          {/* An empty library is said once, with its create actions, by the editor column (MessageBuilder). */}
          {provided.length ? (
            <div className="msgb-railblock">
              <SectionLabel>{t("messaging.builder.defaultLayouts", "Default layouts")}</SectionLabel>
              <ul className="msgb-list">{provided.map(row)}</ul>
            </div>
          ) : null}
          {/* Custom layouts appear only once the workspace has made one (New layout above makes them). */}
          {own.length ? (
            <div className="msgb-railblock">
              <SectionLabel>{t("messaging.builder.customLayouts", "Custom layouts")}</SectionLabel>
              <ul className="msgb-list">{own.map(row)}</ul>
            </div>
          ) : null}
          {archived.length ? (
            <div className="msgb-railblock">
              <SectionLabel>{t("messaging.builder.archived", "Archived")}</SectionLabel>
              <ul className="msgb-list">{archived.map(row)}</ul>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
